import React, { useState } from 'react';
import { Eye, EyeOff, Key, Loader2, Mail, Shield, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Input, VerificationCodeInput } from '../../components/forms';
import {
  useGetProfileQuery,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useCancelEmailChangeMutation,
} from '../../services/profileApi';
import { authService } from '../../services/authService';
import { useToast } from '../../contexts/ToastContext';

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface EmailChangeFormData {
  newEmail: string;
  password: string;
}

const ProfileSecurity: React.FC = () => {
  const { success, error: showError } = useToast();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showEmailChangeForm, setShowEmailChangeForm] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const { data: profile, refetch } = useGetProfileQuery();
  const [requestEmailChange, { isLoading: isRequestingEmailChange }] =
    useRequestEmailChangeMutation();
  const [verifyEmailChange, { isLoading: isVerifyingEmail }] = useVerifyEmailChangeMutation();
  const [cancelEmailChange, { isLoading: isCancellingEmail }] = useCancelEmailChangeMutation();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
    watch,
  } = useForm<PasswordFormData>();

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
    formState: { errors: emailErrors },
  } = useForm<EmailChangeFormData>();

  const newPassword = watch('newPassword');

  const onPasswordSubmit = async (data: PasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await authService.changePassword(data.currentPassword, data.newPassword);

      if (response.success) {
        success('Password changed successfully');
        resetPasswordForm();
      } else {
        showError(response.message || 'Failed to change password');
      }
    } catch (err: any) {
      showError(err?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const onEmailChangeRequest = async (data: EmailChangeFormData) => {
    try {
      const result = await requestEmailChange({
        newEmail: data.newEmail,
        password: data.password,
      }).unwrap();

      setMaskedEmail(result.maskedEmail);
      setShowEmailChangeForm(false);
      setShowOtpVerification(true);
      success(`Verification code sent to ${result.maskedEmail}`);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to request email change');
    }
  };

  const handleVerifyEmailChange = async () => {
    if (verificationCode.length < 4) {
      showError('Please enter the verification code');
      return;
    }

    try {
      await verifyEmailChange({ otp: verificationCode }).unwrap();
      success('Email changed successfully');
      setShowOtpVerification(false);
      setVerificationCode('');
      resetEmailForm();
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Invalid verification code');
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      await cancelEmailChange().unwrap();
      setShowOtpVerification(false);
      setShowEmailChangeForm(false);
      setVerificationCode('');
      resetEmailForm();
      success('Email change cancelled');
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to cancel email change');
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: '', color: 'bg-gray-200' };

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { strength, label: 'Fair', color: 'bg-amber-500' };
    if (strength <= 4) return { strength, label: 'Good', color: 'bg-green-500' };
    return { strength, label: 'Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength(newPassword || '');

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Security</h1>
        <p className="text-sm text-gray-500 mt-0.5">Manage your password and email settings</p>
      </div>

      {/* Password Change Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Key className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Change Password</h2>
            <p className="text-xs text-gray-500">Update your password regularly for security</p>
          </div>
        </div>

        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
          <div className="space-y-4 max-w-md">
            {/* Current Password */}
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                label="Current Password"
                {...registerPassword('currentPassword', {
                  required: 'Current password is required',
                })}
                error={passwordErrors.currentPassword?.message}
                placeholder="Enter your current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                label="New Password"
                {...registerPassword('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                error={passwordErrors.newPassword?.message}
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full ${
                        i <= passwordStrength.strength ? passwordStrength.color : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Password strength: <span className="font-medium">{passwordStrength.label}</span>
                </p>
              </div>
            )}

            {/* Confirm Password */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm New Password"
                {...registerPassword('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => value === newPassword || 'Passwords do not match',
                })}
                error={passwordErrors.confirmPassword?.message}
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={isChangingPassword}
              className="px-6 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Changing...</span>
                </>
              ) : (
                <span>Change Password</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Email Change Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Mail className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Email Address</h2>
            <p className="text-xs text-gray-500">Your current email: {profile?.email}</p>
          </div>
        </div>

        {/* Pending Email Change */}
        {profile?.pendingEmail && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-amber-800">Email change pending</p>
                <p className="text-sm text-amber-700 mt-1">
                  A verification code was sent to your new email address.
                </p>
              </div>
              <button
                onClick={handleCancelEmailChange}
                disabled={isCancellingEmail}
                className="text-amber-600 hover:text-amber-800"
              >
                {isCancellingEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* OTP Verification */}
        {showOtpVerification ? (
          <div className="max-w-md">
            <p className="text-sm text-gray-600 mb-4">
              Enter the verification code sent to {maskedEmail}
            </p>
            <div className="mb-4">
              <VerificationCodeInput length={4} onChange={setVerificationCode} />
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleVerifyEmailChange}
                disabled={isVerifyingEmail || verificationCode.length < 4}
                className="px-6 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isVerifyingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <span>Verify & Change Email</span>
                )}
              </button>
              <button
                onClick={handleCancelEmailChange}
                disabled={isCancellingEmail}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : showEmailChangeForm ? (
          <form onSubmit={handleEmailSubmit(onEmailChangeRequest)} className="max-w-md">
            <div className="space-y-4">
              <Input
                type="email"
                label="New Email Address"
                {...registerEmail('newEmail', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                })}
                error={emailErrors.newEmail?.message}
                placeholder="Enter your new email address"
              />

              <Input
                type="password"
                label="Current Password"
                {...registerEmail('password', {
                  required: 'Password is required to change email',
                })}
                error={emailErrors.password?.message}
                placeholder="Enter your password to confirm"
              />

              <div className="flex items-center space-x-3">
                <button
                  type="submit"
                  disabled={isRequestingEmailChange}
                  className="px-6 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isRequestingEmailChange ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Verification Code</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailChangeForm(false);
                    resetEmailForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowEmailChangeForm(true)}
            className="px-6 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            Change Email Address
          </button>
        )}
      </div>

      {/* Security Tips */}
      <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-2 mb-3">
          <Shield className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">Security Tips</h3>
        </div>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>
              Use a strong password with at least 8 characters, including uppercase, lowercase,
              numbers, and symbols.
            </span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>
              Never share your password with anyone or use the same password across multiple
              accounts.
            </span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Check your activity log regularly for any unauthorized access.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfileSecurity;
